import { injectable, inject } from 'inversify';
import Request from '@framework/api/Rpc/Request';
import { type IDispatcher } from '@users/json-rpc-dispatcher';
import DITypes from '@users/json-rpc-dispatcher/DITypes';
import { RpcSuccess } from '@dealroadshow/json-rpc-dispatcher';
import { IUser, UserId } from '@users/user/vo/User';

@injectable()
class UserRepository {
  constructor(@inject(DITypes.JsonRpcDispatcher) private readonly rpc: IDispatcher) {}

  list = async (data: Object = {}) => {
    const request = new Request('user.list', data);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  };

  /**
   * Register user
   */
  async register(data: Object) {
    const request = new Request('user.register', data);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  }

  /**
   * Complete user profile
   */
  async completeProfile(data: Object) {
    const request = new Request('user.complete_profile', data);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  }

  async completeInvestorProfile(userData: Object, ssid: string | null = null) {
    const request = new Request('user.complete_investor_profile', userData, { ...(ssid && { ssid }) });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  }

  /**
   * Request reset password
   */
  async sendForgotPasswordEmail(data: Object) {
    const request = new Request('user.send_forgot_password_email', data);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  }

  /**
   * Reset password
   */
  async resetPassword(data: Object) {
    const request = new Request('user.reset_password', data);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  }

  /**
   * Resend activation email
   */
  async resendActivationEmail(data: Object) {
    const request = new Request('user.resend_activation_email', data);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  }

  getUserById = async (id: UserId, aggregateCompany = false): Promise<IUser> => {
    const request = new Request('user.get_by_id', { id, aggregateCompany });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  };

  async getUserByActivationCode(email: string, activationCode: string, aggregateCompany = false) {
    const request = new Request('user.get_by_activation_code', { email, activationCode, aggregateCompany });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  }

  async update(userData: Object) {
    const request = new Request('user.update', userData);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  }

  async updatePassword(oldPassword: string, newPassword: string) {
    const request = new Request('user.update_password', { oldPassword, newPassword });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  }

  async verifyPassword(password: string) {
    const request = new Request('user.verify_password', { password });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  }

  async regenerateForgotPasswordUrl(id: UserId, tenant: string, callbackUrl: string) {
    const request = new Request('user.regenerate_forgot_password_url', { id, tenant, callbackUrl });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().url;
  }

  async regeneratePassword(id: UserId) {
    const request = new Request('user.regenerate_password', { id });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  }

  async getActivationUrl(email: string, tenant: string, callbackUrl: string) {
    const request = new Request('user.get_activation_url', { email, tenant, callbackUrl });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().url;
  }

  async regenerateActivationCode(email: string) {
    const request = new Request('user.regenerate_activation_code', { email });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  }

  edit = async (userData: Object) => {
    const request = new Request('user.edit', { ...userData });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  };

  /**
   * Check email domain validation
   */
  checkEmailDomainValidation = async (email: string): Promise<boolean> => {
    const request = new Request('account.queries.is_email_domain_valid', { email });
    const response = await this.rpc.call<RpcSuccess>(request);

    return Boolean(response.getResult());
  };
}

export default UserRepository;
